import { DEFAULT_CROP, type CropTransform } from './crop';

/**
 * A photo the user picked, decoded and shrunk to a working copy.
 *
 * Originals are released immediately. Fifteen 12-megapixel phone photos decoded at
 * full size is well over a gigabyte of bitmap, and iOS Safari kills the tab long
 * before that. At this size the whole set is a manageable ~65MB.
 */
export interface Photo {
  id: string;
  /** Downscaled working copy, used for the editor, previews, and export. */
  image: HTMLCanvasElement;
  width: number;
  height: number;
  crop: CropTransform;
  /**
   * Set only for a ball already saved in a published game, naming the image it's
   * already stored under. Such a ball can be moved or removed but not re-cropped: the
   * original photo is long gone from the browser, and this is the cropped, downscaled
   * result. Editing reuses the stored image untouched, so it never re-uploads.
   */
  savedKey?: string;
}

/** Longest edge of the working copy, in pixels. */
const WORKING_MAX = 1200;

export class UnsupportedImageError extends Error {
  constructor(readonly fileName: string) {
    super(`Could not read ${fileName}`);
    this.name = 'UnsupportedImageError';
  }
}

let nextId = 0;

/**
 * Decodes one file into a working copy.
 *
 * Uses an <img> rather than createImageBitmap because Safari's support for the
 * options bag has been unreliable, and an <img> applies EXIF orientation on its own —
 * without which every photo shot in portrait comes out sideways.
 */
export async function loadPhoto(file: File): Promise<Photo> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    try {
      // Rejects on formats the browser can't decode. That's how HEIC is caught:
      // iOS usually transcodes to JPEG on pick, but not always, and no browser
      // decodes HEIC natively.
      await img.decode();
    } catch {
      throw new UnsupportedImageError(file.name);
    }

    const sw = img.naturalWidth;
    const sh = img.naturalHeight;
    if (!sw || !sh) throw new UnsupportedImageError(file.name);

    const scale = Math.min(1, WORKING_MAX / Math.max(sw, sh));
    const width = Math.max(1, Math.round(sw * scale));
    const height = Math.max(1, Math.round(sh * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new UnsupportedImageError(file.name);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    return {
      id: `photo-${nextId++}`,
      image: canvas,
      width,
      height,
      crop: DEFAULT_CROP,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Loads a ball already saved in a game, so the edit screen can show the real roster.
 *
 * The stored image is square and already cropped, so it needs no crop of its own —
 * DEFAULT_CROP draws it exactly as the game does.
 */
export async function loadSavedBall(key: string): Promise<Photo> {
  const img = new Image();
  img.src = `/${key}`;
  try {
    await img.decode();
  } catch {
    throw new UnsupportedImageError(key);
  }

  const size = img.naturalWidth || 1;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = img.naturalHeight || size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UnsupportedImageError(key);
  ctx.drawImage(img, 0, 0);

  return {
    id: `saved-${key}`,
    image: canvas,
    width: canvas.width,
    height: canvas.height,
    crop: DEFAULT_CROP,
    savedKey: key,
  };
}

export interface LoadResult {
  photos: Photo[];
  /** Names of files that couldn't be decoded, for a single grouped message. */
  rejected: string[];
}

/**
 * Decodes a batch, keeping the good ones and reporting the rest.
 *
 * Sequential on purpose: decoding fifteen photos in parallel spikes memory hard
 * enough to get the tab killed on a phone, and the whole batch takes about the same
 * wall-clock time either way.
 */
export async function loadPhotos(files: File[], limit: number): Promise<LoadResult> {
  const photos: Photo[] = [];
  const rejected: string[] = [];

  for (const file of files.slice(0, limit)) {
    try {
      photos.push(await loadPhoto(file));
    } catch {
      rejected.push(file.name);
    }
  }

  return { photos, rejected };
}
